import {
  InformationOutline18Icon,
  LinkOutOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { ISirkModuleForShifts } from "features/CenterContent/RoleContent/ISirk/ISirkModuleForShifts";
import { PanelActions } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import {
  BusModule,
  PanelHeaderDivider,
  PanelHorizontalScroll,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { useVehicleReplacementAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/VehicleReplacement";
import { ShiftSchedule } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ShiftSchedule";
import { useMemo } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import {
  Badge,
  Button,
  HStack,
  Text,
  tokens,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "shared/components/Spor";
import {
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";
import { LANDING } from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

interface AlternativeTransportLeftPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportLeftPanel = ({
  vehicle,
}: AlternativeTransportLeftPanelProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { role } = useDropsRole();

  const distributionStopReferences: Array<StopReference> = useMemo(() => {
    const stops = vehicle?.itinerary?.stops ?? [];
    const returnStops = vehicle?.itinerary?.returnStops ?? [];
    return [...stops, ...returnStops].map(
      (value: AlternativeTransportItineraryStop) => value.stopReference,
    );
  }, [vehicle]);

  const replacementAction = useVehicleReplacementAction({
    vehicle,
    distributionStopReferences,
  });

  const { workShiftPlan, operatorName } = vehicle;
  const { workShiftTrips, shiftId, shiftDate, isirkId } = workShiftPlan ?? {};

  const firstDeparture = workShiftTrips?.at(0)?.scheduledDepartureTimeOrigin;
  const lastArrival = workShiftTrips?.at(-1)?.scheduledArrivalTimeDestination;
  const timeInterval =
    firstDeparture && lastArrival
      ? `${firstDeparture.toLocaleTimeString("no-NO", {
          hour: "2-digit",
          minute: "2-digit",
        })}–${lastArrival.toLocaleTimeString("no-NO", {
          hour: "2-digit",
          minute: "2-digit",
        })}`
      : "";

  return (
    <BusModule
      modulePosition="left"
      title={<Text variant="md-lg">{`Skift: ${shiftId ?? ""}`}</Text>}
      sectionState={{
        isEmpty: !vehicle.workShiftPlan,
        emptyReason: "Ikke knyttet til skift",
      }}
      action1={
        <PanelActions
          overflowActions={[replacementAction]}
          inlineActions={[replacementAction]}
        />
      }
    >
      <PanelHeaderDivider>
        {operatorName && workShiftTrips ? (
          <>
            <Text color={theme.colorTextSecondary}>{operatorName}</Text>
            <HStack>
              <Text>{timeInterval}</Text>
              <Text>({shiftDate?.toLocaleDateString("no-NO")})</Text>
            </HStack>
          </>
        ) : (
          <NoDataMessage reason="Det finnes ikke skiftinfo denne bussen" />
        )}
      </PanelHeaderDivider>
      <PanelHorizontalScroll>
        <PanelScrollContent>
          <PanelScrollArea>
            <PanelModuleGrid>
              <Module title="Skiftplan">
                <ShiftSchedule vehicle={vehicle} />
              </Module>

              <Module
                title="I-sirk-logg"
                badgeLeft1={
                  <Badge colorPalette="green" size="md">
                    {isirkId || "ukjent"}
                  </Badge>
                }
                size="md"
                action1={
                  <Button
                    size="xs"
                    fontSize="xs"
                    variant="tertiary"
                    onClick={() =>
                      navigate(
                        generatePath(LANDING, {
                          roleIdParam: role,
                          tabIdParam: "start",
                        }),
                      )
                    }
                  >
                    I-sirk-logg <LinkOutOutline18Icon />
                  </Button>
                }
                action2={
                  <Tooltip>
                    <TooltipTrigger>
                      <InformationOutline18Icon />
                    </TooltipTrigger>
                    <TooltipContent
                      showArrow
                      maxWidth="220px"
                      color={theme.colorBackgroundMain}
                      backgroundColor={theme.colorSuccessTextSecondary}
                      padding={tokens.default.size.spacing[1]}
                      borderRadius={tokens.default.size["border-radius"].sm}
                    >
                      Denne loggen sendes til i-sirkloggen. Her vises kun det
                      som er lagt inn på dette skiftet. Kundeveiledere har ikke
                      tilgang til denne loggen.
                    </TooltipContent>
                  </Tooltip>
                }
              >
                <ISirkModuleForShifts
                  selectedDate={shiftDate?.toString() ?? ""}
                  shiftId={shiftId ?? ""}
                  operatorName={operatorName || ""}
                />
              </Module>
            </PanelModuleGrid>
          </PanelScrollArea>
        </PanelScrollContent>
      </PanelHorizontalScroll>
    </BusModule>
  );
};
